// Web Component `<kyp-capture>`: instancia `KypCapture` sobre sí mismo (Shadow DOM) y reemite sus
// eventos (`step | progress | complete | rejected | error`) desde el elemento.
import { KypCapture, type KypCaptureOptions } from '../capture.js';
import { normalizeLocale } from '../i18n/index.js';

const EVENTS = ['step', 'progress', 'complete', 'rejected', 'error'] as const;

/** Base segura para entornos sin DOM (SSR, Node): la clase existe pero no se registra. */
const Base = (typeof HTMLElement === 'undefined' ? Object : HTMLElement) as typeof HTMLElement;

/**
 * `<kyp-capture api-base="…" locale="es" voice ruler-url="/regla-kyp.html" mode="auto" test-hooks
 *   handoff-id="hnd_…" handoff-token="…">` (los dos últimos: móvil que llega por el QR).
 * Arranca al conectarse (si tiene `api-base`) y se destruye al desconectarse. La instancia está en
 * `element.capture`.
 */
export class KypCaptureElement extends Base {
  static get observedAttributes(): string[] {
    return [
      'api-base',
      'locale',
      'voice',
      'ruler-url',
      'mode',
      'test-hooks',
      'handoff-id',
      'handoff-token',
    ];
  }

  /** Instancia en curso (o `null`). */
  capture: KypCapture | null = null;

  connectedCallback(): void {
    if (!this.capture && this.getAttribute('api-base')) this.boot();
  }

  disconnectedCallback(): void {
    this.capture?.destroy();
    this.capture = null;
  }

  attributeChangedCallback(name: string, oldValue: string | null, value: string | null): void {
    // Cambiar la configuración reinicia la captura (solo si ya estaba conectado).
    if (oldValue === value || !this.isConnected) return;
    if (name === 'api-base' || this.capture) {
      this.capture?.destroy();
      this.capture = null;
      if (this.getAttribute('api-base')) this.boot();
    }
  }

  /** Opciones a partir de los atributos. */
  options(): KypCaptureOptions {
    const mode = this.getAttribute('mode');
    const rulerUrl = this.getAttribute('ruler-url');
    const handoffId = this.getAttribute('handoff-id');
    const handoffToken = this.getAttribute('handoff-token');
    return {
      apiBase: this.getAttribute('api-base') ?? '',
      locale: normalizeLocale(
        this.getAttribute('locale') ?? globalThis.document?.documentElement.lang,
      ),
      container: this,
      voice: this.hasAttribute('voice'),
      testHooks: this.hasAttribute('test-hooks'),
      ...(mode === 'desktop' || mode === 'mobile' || mode === 'auto' ? { mode } : {}),
      ...(rulerUrl ? { rulerUrl } : {}),
      ...(handoffId && handoffToken ? { handoff: { id: handoffId, token: handoffToken } } : {}),
    };
  }

  private boot(): void {
    const capture = new KypCapture(this.options());
    this.capture = capture;
    for (const type of EVENTS)
      capture.addEventListener(type, (ev: Event) => {
        this.dispatchEvent(
          new CustomEvent(type, {
            detail: (ev as CustomEvent).detail,
            bubbles: true,
            composed: true,
          }),
        );
      });
    void capture.start();
  }
}

/** Registra el elemento con `tag` (por defecto `kyp-capture`) si no lo está ya. */
export function defineKypCaptureElement(tag = 'kyp-capture'): void {
  if (typeof customElements === 'undefined' || customElements.get(tag)) return;
  // Una subclase por etiqueta: el registro no admite la misma clase dos veces.
  customElements.define(tag, class extends KypCaptureElement {});
}
